import React, { useEffect, useState, useCallback } from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { colors } from '../Theme';

import CloseIcon from '../../../../public/static/images/icons/close.svg';

const WIDTH = '660px';

const overlayStyles = css`
  :global(.overflow-disabled) {
    overflow: hidden;
  }

  .overlay {
    position: fixed;
    display: flex;
    padding: 5px;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    justify-content: center;
    align-items: center;
    overflow: auto;
    z-index: 9999;
  }

  .overlay--displayed {
    background-color: ${colors.backdrop};
  }

  .overlay-content {
    position: relative;
    display: flex;
    padding: 40px 24px;
    max-width: 100%;
    min-height: 98vh;
    flex-direction: column;
    align-self: flex-start;
    justify-content: center;
    background-color: white;
    box-shadow: 0 0 16px 0 ${colors.boxShadow};
    box-sizing: border-box;
    animation-duration: 0.3s;
    animation-name: slidein;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .overlay-content--hidden {
    transform: translate3d(0, -250%, 0);
    opacity: 0;
  }

  .overlay-close-button {
    position: absolute;
    padding: 20px;
    top: 0;
    right: 0;
    border: none;
    background: none;
  }

  @keyframes slidein {
    from {
      transform: translate3d(0, -100%, 0);
    }

    to {
      transform: translate3d(0, 0, 0);
    }
  }

  @media (min-width: ${WIDTH}) {
    .overlay-content {
      margin: 0;
      width: ${WIDTH};
      min-height: 40vh;
      align-self: center;
    }
  }
`;

const overlayContentStyles = css.resolve`
  .overlay-text {
    font-size: 1.4rem;
    line-height: 1.2;
  }

  .overlay-button {
    min-width: 100px;
    align-self: center;
  }
`;

const Overlay = ({ onClose, children, className, contentClassName }) => {
  const bodyClassOverflowDisabled = 'overflow-disabled';
  const [closing, setClosing] = useState(false);
  const closeOverlay = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove(bodyClassOverflowDisabled);
      onClose(true);
    }, 250);
  }, [onClose]);
  const overlayClassName = overlayContentStyles.className;
  useEffect(() => {
    document.body.classList.add(bodyClassOverflowDisabled);
    window.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        closeOverlay();
      }
    });
  }, [closeOverlay]);
  return (
    <>
      <div
        className={classnames(className, 'overlay', {
          'overlay--displayed': !closing
        })}
      >
        <div
          className={classnames('overlay-content', contentClassName, {
            'overlay-content--hidden': closing
          })}
        >
          <button
            type="button"
            className="overlay-close-button"
            onClick={closeOverlay}
          >
            <CloseIcon />
            <span className="sr-only">Fermer</span>
          </button>
          {children(overlayClassName, closeOverlay)}
        </div>
      </div>
      <style jsx>{overlayStyles}</style>
      {overlayContentStyles.styles}
    </>
  );
};

Overlay.propTypes = {
  onClose: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
  className: PropTypes.string,
  contentClassName: PropTypes.string
};

export default Overlay;
